import React from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../utils/mq';

import Loader from '../../icons/loader';
import Disk from '../../icons/disk';
import { useTranslation } from '../../utils/translation';
import { ProgressBar } from '../../atoms/progress-bar';

const Button = styled.div`
  align-items: center;
  background-color: var(--theme-palette-green);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: var(--theme-typography-primary-font);
  font-weight: bold;
  justify-content: center;
  gap: 73px;
  margin: 10px 0 0;
  padding: 15px 25px;
  position: relative;
  transition: background-color 0.3s ease;
  width: 100%;

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 24px;
      margin: 10px 0 5px;
    `,
  )}
`;

const ButtonIcon = styled.div`
  align-items: center;
  display: flex;
  gap: 15px;
`;

const ButtonRight = styled.div`
  display: flex;
  margin: 0 0 0 auto;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: initial;
      position: absolute;
      right: 20px;
    `,
  )}
`;

const Information = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacer-xs);
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 var(--spacer-s);
`;

export type DownloadButtonProps = {
  isLoading: boolean;
  thumbnailsOs: { [key: string]: any };
  os: string;
  fileId: string;
  callback: (id: string, os: string) => void;
};

export const DownloadButton = ({ ...props }: DownloadButtonProps) => {
  const { isLoading, thumbnailsOs, os, callback, fileId } = props;
  const t = useTranslation();

  const Icon = thumbnailsOs[os];
  let Content = os !== 'Web' && <Disk width={20} height={20} color="var(--theme-palette-white)" />;

  if (isLoading) {
    Content = <Loader width={24} height={24} color="var(--theme-palette-white)" />;
  }

  return isLoading ? (
    <>
      <Information>
        {t('design.download.inProgress.loading')}
        <Icon color="var(--title-foreground)" width={15} height={15} />
      </Information>
      <ProgressBar />
    </>
  ) : (
    <Button onClick={() => callback(fileId, os)}>
      <ButtonIcon>
        <Icon color="var(--theme-palette-white)" width={20} height={20} />
        {t('design.download.inProgress.loaded')}
      </ButtonIcon>
      <ButtonRight>{Content}</ButtonRight>
    </Button>
  );
};
